/**
 * MpciApiClient.js
 *
 * Reusable native-fetch API client for all MPCI modules.
 * Follows the same pattern as ABI (EditTradePartner, api-fetch.js):
 *   - Native browser fetch
 *   - Bearer token from localStorage
 *   - No cng-web-lib dependency
 *
 * MPCI BE response shapes:
 *   GET by id  → { status, message, data: { ...record } }
 *   POST list  → { status, message, data: { content: [], totalElements, ... } }
 *   POST/PUT   → { status, message, data: { ...record } }
 */

const getAuthHeaders = () => ({
  'Content-Type': 'application/json',
  Authorization: 'Bearer ' + JSON.parse(localStorage.getItem('userToken'))
})

// Auth header without Content-Type — used for multipart so browser sets boundary automatically
const getAuthHeadersOnly = () => ({
  Authorization: 'Bearer ' + JSON.parse(localStorage.getItem('userToken'))
})

/**
 * GET {url}/{id}
 * e.g. GET /mpci/master-bill/get/48268a926224b5f7e18deede9067b821
 *
 * @param {string}   url        - base endpoint (e.g. UaeMpciApiUrls.MASTER_BILL_GET)
 * @param {string}   id         - record id (path param)
 * @param {Function} onSuccess  - (data) => void  — receives response.data
 * @param {Function} onError    - (error) => void
 */
const getById = (url, id, onSuccess, onError) => {
  fetch(`${url}/${id}`, {
    method: 'GET',
    headers: getAuthHeaders()
  })
    .then((res) => {
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      return res.json()
    })
    .then((json) => {
      if (onSuccess) onSuccess(json?.data ?? null)
    })
    .catch((err) => {
      console.error('[MpciApiClient.getById]', err)
      if (onError) onError(err)
    })
}

/**
 * POST {url}  — create / list / submit / cancel etc.
 *
 * @param {string}   url        - endpoint
 * @param {object}   body       - request payload
 * @param {Function} onSuccess  - (data) => void
 * @param {Function} onError    - (error) => void
 */
const post = (url, body, onSuccess, onError) => {
  const hasBody = body !== null && body !== undefined
  fetch(url, {
    method: 'POST',
    headers: getAuthHeaders(),
    ...(hasBody && { body: JSON.stringify(body) })
  })
    .then((res) => {
      const contentType = res.headers.get('Content-Type') || ''
      const isJson = contentType.includes('application/json')
      if (isJson) {
        return res.json().then((json) => ({ ok: res.ok, status: res.status, json }))
      }
      return { ok: res.ok, status: res.status, json: null }
    })
    .then(({ ok, status, json }) => {
      if (!ok) {
        const apiErr = { status: json?.status ?? status, message: json?.message ?? `HTTP ${status}` }
        console.error('[MpciApiClient.post] error response', apiErr)
        if (onError) onError(apiErr)
        return
      }
      if (onSuccess) onSuccess(json?.data ?? null)
    })
    .catch((err) => {
      console.error('[MpciApiClient.post]', err)
      if (onError) onError({ status: null, message: err.message })
    })
}

/**
 * PUT {url}  — update record
 *
 * @param {string}   url        - endpoint
 * @param {object}   body       - request payload
 * @param {Function} onSuccess  - (data) => void
 * @param {Function} onError    - (error) => void
 */
const put = (url, body, onSuccess, onError) => {
  fetch(url, {
    method: 'PUT',
    headers: getAuthHeaders(),
    body: JSON.stringify(body)
  })
    .then((res) => {
      return res.json().then((json) => ({ ok: res.ok, status: res.status, json }))
    })
    .then(({ ok, status, json }) => {
      if (!ok) {
        const apiErr = { status: json?.status ?? status, message: json?.message ?? `HTTP ${status}` }
        console.error('[MpciApiClient.put] error response', apiErr)
        if (onError) onError(apiErr)
        return
      }
      if (onSuccess) onSuccess(json?.data ?? null)
    })
    .catch((err) => {
      console.error('[MpciApiClient.put]', err)
      if (onError) onError({ status: null, message: err.message })
    })
}

/**
 * PUT {url}/{id}  — update/submit with path param id, sent as multipart/form-data
 * Payload is appended as a JSON Blob under the key 'data' (per BE API contract).
 * Optional files array is appended under the key 'files'.
 * Browser sets Content-Type + boundary automatically — DO NOT set Content-Type header.
 *
 * @param {string}   url        - base endpoint
 * @param {string}   id         - record id (path param)
 * @param {object}   body       - request payload
 * @param {File[]}   files      - optional supporting document files
 * @param {Function} onSuccess  - (data) => void
 * @param {Function} onError    - (error) => void
 */
const putById = (url, id, body, files, onSuccess, onError) => {
  const formData = new FormData()
  formData.append(
    'data',
    new Blob([JSON.stringify(body)], { type: 'application/json' })
  )
  ;(files || []).forEach((file) => {
    formData.append('files', file)
  })

  fetch(`${url}/${id}`, {
    method: 'PUT',
    headers: getAuthHeadersOnly(),
    body: formData
  })
    .then((res) => {
      return res.json().then((json) => ({ ok: res.ok, status: res.status, json }))
    })
    .then(({ ok, status, json }) => {
      if (!ok) {
        const apiErr = { status: json?.status ?? status, message: json?.message ?? `HTTP ${status}` }
        console.error('[MpciApiClient.putById] error response', apiErr)
        if (onError) onError(apiErr)
        return
      }
      if (onSuccess) onSuccess(json?.data ?? null)
    })
    .catch((err) => {
      console.error('[MpciApiClient.putById]', err)
      if (onError) onError({ status: null, message: err.message })
    })
}

/**
 * DELETE {url}/{id}  — path param delete per BE @DeleteMapping("/delete/{id}")
 * BE response: { status, message, data: string|object }
 *
 * onSuccess is called outside the Promise chain to prevent any exception
 * thrown inside onSuccess from leaking into .catch() and falsely triggering onError.
 *
 * @param {string}   url        - full endpoint including /{id}
 * @param {object}   body       - request payload (usually {})
 * @param {Function} onSuccess  - () => void
 * @param {Function} onError    - (error) => void
 */
const del = (url, body, onSuccess, onError) => {
  fetch(url, {
    method: 'DELETE',
    headers: getAuthHeaders(),
    body: JSON.stringify(body)
  })
    .then((res) => {
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      return res.json()
    })
    .then((json) => {
      // Call onSuccess via setTimeout to break out of the Promise chain.
      // This prevents any exception inside onSuccess (e.g. tableRef.current.performRefresh())
      // from propagating to .catch() and falsely firing onError.
      setTimeout(() => { if (onSuccess) onSuccess(json?.data ?? null) }, 0)
    })
    .catch((err) => {
      console.error('[MpciApiClient.del]', err)
      if (onError) onError(err)
    })
}

/**
 * GET {url}  — plain GET with optional query string already in url
 *
 * @param {string}   url        - full endpoint (with query params if needed)
 * @param {Function} onSuccess  - (data) => void  — receives response.data
 * @param {Function} onError    - (error) => void
 */
const get = (url, onSuccess, onError) => {
  fetch(url, {
    method: 'GET',
    headers: getAuthHeaders()
  })
    .then((res) => {
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      return res.json()
    })
    .then((json) => {
      if (onSuccess) onSuccess(json?.data ?? null)
    })
    .catch((err) => {
      console.error('[MpciApiClient.get]', err)
      if (onError) onError(err)
    })
}

const MpciApiClient = { get, getById, post, put, putById, del }

export default MpciApiClient